/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import Fade from "@mui/material/Fade";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import classnames from "classnames";

import { IOrganization } from "@formant/core/model/IOrganization";
import { Button } from "@formant/ui-kit";

import { organizationClient } from "../../../clients";
import { useStores } from "../../../context/StoreContext";
import { formatAmount } from "../../../utils/formatAmount";
import { Heading3 } from "../../common/Heading3";
import { IPopupProps, Popup } from "../../common/Popup";
import { RadioGroup } from "../../common/RadioGroup";
import { Switch } from "../../common/Switch";
import * as styles from "./index.scss";

export interface ISetBudgetPopupProps {
    popupProps: IPopupProps;
    organization: IOrganization;
}

const selectedOptions = {
    fixed: { value: "Fixed monthly budget" },
    variable: {
        value: "Variable budget based on number of devices",
    },
};

const minAmount = 1;

export function SetBudgetPopup({
    popupProps,
    organization,
}: ISetBudgetPopupProps) {
    const { organizationStore, deviceStore, authStore } = useStores();

    const [selectedOption, setSelectedOption] = useState<string | undefined>(
        selectedOptions.fixed.value,
    );

    const [isNotificationOn, setIsNotificationOn] = useState(true);

    const [amount, setAmount] = useState<string>("");

    const devicesPerBudget =
        deviceStore.list()?.filter(device => device.enabled)?.length || 0;
    const onCloseModal = () => {
        popupProps.onClose?.();
    };

    const onNotificationChange = useCallback(() => {
        setIsNotificationOn(val => !val);
    }, [setIsNotificationOn]);

    const onAmountChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            setAmount(
                (parseFloat(event.target.value) || 0) <= 0
                    ? `${minAmount}`
                    : event.target.value,
            );
        },
        [setAmount],
    );

    const onSaveBudget = useCallback(async () => {
        if (!organization.id) {
            return;
        }

        const billingInfo = {
            ...organization.billingInfo,
            ...(!isNotificationOn && {
                deviceBudgetUsdCents: null,
                organizationBudgetUsdCents: null,
            }),
            ...(selectedOption === selectedOptions.fixed.value &&
                isNotificationOn && {
                    deviceBudgetUsdCents: null,
                    organizationBudgetUsdCents: formatAmount(
                        "dollar-to-cent",
                        parseFloat(amount),
                    ),
                }),
            ...(selectedOption === selectedOptions.variable.value &&
                isNotificationOn && {
                    deviceBudgetUsdCents: formatAmount(
                        "dollar-to-cent",
                        parseFloat(amount),
                    ),
                    organizationBudgetUsdCents: null,
                }),
        };
        await organizationStore.update(organization.id, {
            billingInfo,
        });

        onCloseModal();
    }, [
        amount,
        selectedOption,
        organization.id,
        organization.billingInfo.usagePrices,
        organizationClient,
        authStore,
        onCloseModal,
    ]);

    useEffect(() => {
        const deviceBudget = organization.billingInfo.deviceBudgetUsdCents;
        const organizationBudget =
            organization.billingInfo.organizationBudgetUsdCents;

        if (deviceBudget) {
            setAmount(`${formatAmount("cent-to-dollar", deviceBudget)}`);
            setIsNotificationOn(true);
            setSelectedOption(selectedOptions.variable.value);
        } else if (organizationBudget) {
            setAmount(`${formatAmount("cent-to-dollar", organizationBudget)}`);
            setIsNotificationOn(true);
            setSelectedOption(selectedOptions.fixed.value);
        } else {
            setIsNotificationOn(false);
        }
    }, [
        organization.billingInfo.deviceBudgetUsdCents,
        organization.billingInfo.organizationBudgetUsdCents,
        popupProps.visible,
    ]);

    const boxClassNames = classnames(
        styles["popup-content-padding"],
        styles["budget-amount-container"],
    );

    return (
        <Popup {...popupProps} variant="dark" onClose={undefined}>
            <Grid
                className={classnames(
                    styles["spend-alert-padding"],
                    styles["budget-amount-container"],
                )}
                container={true}
                justifyContent="space-between"
                alignItems="center"
                xs={12}
                item={true}
            >
                <Grid item={true}>
                    <h4 className={styles["spend-alert-heading"]}>
                        spend alerts
                    </h4>
                </Grid>

                <Grid display="inline-flex" item={true}>
                    <Heading3 className={styles["action-text"]}>
                        {isNotificationOn ? "ON" : "OFF"}
                    </Heading3>
                    <Switch
                        state={isNotificationOn}
                        onChange={onNotificationChange}
                    />
                </Grid>
            </Grid>

            <div className={styles["popup-content"]}>
                <Fade
                    in={!isNotificationOn}
                    timeout={{ enter: 1000, exit: 0 }}
                    unmountOnExit={true}
                    className={styles["fade-container"]}
                >
                    <div>
                        <p
                            className={classnames(
                                styles["dark-text"],
                                styles["popup-content-padding"],
                            )}
                        >
                            {`Turn on alerts to be emailed when your 
                spend is nearing your budget.`}
                        </p>
                    </div>
                </Fade>

                <Fade
                    in={isNotificationOn}
                    timeout={{ enter: 1000, exit: 0 }}
                    unmountOnExit={true}
                    className={styles["fade-container"]}
                >
                    <div>
                        <p
                            className={classnames(
                                styles["dark-text"],
                                styles["popup-content-padding"],
                            )}
                        >
                            We’ll send emails when your spend reaches 80%, 90%,
                            and 100% of your budget.
                        </p>
                        <RadioGroup
                            vAlign="top"
                            className={styles["radio-group"]}
                            value={selectedOption}
                            options={[
                                selectedOptions.fixed,
                                selectedOptions.variable,
                            ]}
                            onChange={setSelectedOption}
                        />
                        {/* @ts-ignore */}
                        <Box className={boxClassNames}>
                            <Heading3 className={styles["budget-amount-text"]}>
                                Budget
                                <TextField
                                    InputProps={{
                                        inputProps: { min: minAmount },
                                        startAdornment: (
                                            <InputAdornment
                                                classes={{
                                                    root: styles[
                                                        "input-adornment"
                                                    ],
                                                }}
                                                position="start"
                                            >
                                                $
                                            </InputAdornment>
                                        ),
                                    }}
                                    className={
                                        styles["budget-amount-text-field"]
                                    }
                                    placeholder="amount"
                                    value={amount}
                                    type="number"
                                    variant="standard"
                                    size="small"
                                    onChange={onAmountChange}
                                />
                                {selectedOption === selectedOptions.fixed.value
                                    ? "per month"
                                    : `\u2715 ${devicesPerBudget} devices per month ($${(
                                          (parseFloat(amount) || 0) *
                                          devicesPerBudget
                                      ).toFixed(2)})`}
                            </Heading3>
                        </Box>
                    </div>
                </Fade>
            </div>
            <Grid
                container={true}
                justifyContent="flex-end"
                alignItems="flex-end"
                alignSelf="flex-end"
                xs={12}
                item={true}
                spacing={2}
                className={styles["popup-button-container"]}
            >
                <Grid item={true}>
                    <Button variant="tertiary" onClick={onCloseModal}>
                        Cancel
                    </Button>
                </Grid>
                <Grid item={true}>
                    <Button onClick={onSaveBudget}>Save</Button>
                </Grid>
            </Grid>
        </Popup>
    );
}
